export interface InventoryItem {
  id: number;
  user_id: string;
  name: string;
  norm_name: string;
  price: number;
  stock: number;
  low_stock_threshold: number;
  deleted_at: string | null;
  created_at: string;
  modified_at: string;
}

// PR3 파생 예약: get_inventory_overview_rpc의 items[] 원소. InventoryItem(테이블 컬럼 그대로,
// snake_case) + reserved(파생, RPC가 매 호출 시 계산 — 저장 컬럼 아님). 정렬은 기존 PR2
// fetchActiveItems와 동일(created_at DESC, id DESC tie-break, RPC 상위집합·무회귀).
export interface InventoryOverviewItem extends InventoryItem {
  reserved: number;
}

// get_inventory_overview_rpc의 unmatched[] 원소 — 윈도우 내 주문(상태 무관, RPC v4) 중 활성
// 마스터에 정규화 이름이 없는 것. qty·orderCount·price·lastOrderDate는 전 주문 기준 — 주문
// 활동량 표시 + 원클릭 등록의 기본값(name/price는 등록 시 마스터 seed로 영속화됨, 단순 표시가
// 아님). 예약 반영은 items[].reserved가 입금 전만 따로 집계. 키는 RPC 계약대로 camelCase
// (items와 다름 — items는 테이블 컬럼 미러라 snake, unmatched는 집계 결과라 camel, 양쪽 다
// RPC가 이렇게 내려주는 그대로 미러).
export interface UnmatchedOrderItem {
  name: string;
  normName: string;
  qty: number;
  orderCount: number;
  lastOrderDate: string;
  price: number;
}

// get_inventory_overview_rpc 응답 전체(단일 jsonb). 키 = RPC 계약과 1:1(PR3 constraints 문서
// 단일 진실). uid NULL(비로그인)이면 windowDays만 클램프값이고 나머지는 빈 구조.
export interface InventoryOverview {
  windowDays: number;
  scanTruncated: boolean;
  unmatchedTotal: number;
  items: InventoryOverviewItem[];
  unmatched: UnmatchedOrderItem[];
}

// available(가용) = stock − reserved는 파생값 — 절대 state로 미러 저장하지 않는다(PR3 제약 #7).
// 렌더 시 이 헬퍼로 계산. 순수 함수, 부작용 없음.
export function getAvailableStock(item: Pick<InventoryOverviewItem, 'stock' | 'reserved'>): number {
  return item.stock - item.reserved;
}

// PR7-2: 부족/품절 판정의 단일 정의 — InventoryTable '부족만 보기'와 KPI '부족·품절' 카운트가
// 같은 판정을 써야 한다. 각자 인라인 비교를 두면 기준(가용 vs 현재고, ≤ vs <)이 갈라질 때
// 목록 필터 개수와 KPI 숫자가 어긋난다(판정 중복 금지 — PR7-1 F7 교훈과 동일 원칙).
export function isLowStock(item: Pick<InventoryOverviewItem, 'stock' | 'reserved' | 'low_stock_threshold'>): boolean {
  return getAvailableStock(item) <= item.low_stock_threshold;
}

export type LedgerReason =
  | '입고'
  | '출고'
  | '조정'
  | '신규등록'
  | '엑셀등록'
  | '주문차감'
  | '주문복원'
  | '주문변경';

export interface InventoryLedgerEntry {
  id: number;
  user_id: string;
  item_id: number;
  delta: number;
  reason: LedgerReason;
  memo: string | null;
  order_id: number | null;
  order_nickname: string | null;
  deduction_id: number | null;
  created_at: string;
}

// 원장에 상품명을 붙인 클라이언트 전용 뷰(item_name). DB 컬럼 미러가 아니다 — FK 없는
// 구조(오너 지시)라 PostgREST embed가 불가능해, service가 원장 페이지 조회 후 상품 id로
// 이름을 별도 조회해 머지한다. 매칭 실패(삭제 후 완전 소실 등 이론상 케이스) 시 null.
export interface InventoryLedgerEntryWithName extends InventoryLedgerEntry {
  item_name: string | null;
}

// PR7: 원장(입출고 내역) 서버측 필터. 전부 null = 무필터(기존과 동일 쿼리). 날짜는 KST 달력
// 날짜 'YYYY-MM-DD'(dateFrom/dateTo 둘 다 inclusive) — timestamptz 경계 변환(dateTo의
// exclusive 상한화 포함)은 service 책임이라 UI/훅은 달력 날짜만 다룬다.
export interface LedgerFilters {
  itemId: number | null;
  reason: LedgerReason | null;
  dateFrom: string | null;
  dateTo: string | null;
}

// 필터 초기값/초기화 공용 상수 — 훅 초기 state·필터 바 초기화·페이지 배선이 같은 값을 공유
// (각자 리터럴을 만들면 필드가 추가될 때 어긋나 무필터 판정이 갈라질 수 있다).
export const EMPTY_LEDGER_FILTERS: LedgerFilters = {
  itemId: null,
  reason: null,
  dateFrom: null,
  dateTo: null,
};

// "필터 활성" 판정의 단일 정의 — 필터 바(초기화 버튼 노출)와 페이지(무결과 문구 분기)가 같은
// 판정을 써야 한다. 각자 인라인 조건을 두면 LedgerFilters에 필드가 추가될 때 한쪽만 갱신돼
// 초기화 버튼과 빈 상태 문구가 어긋난다(codexloop PR7 R1 P3 수용).
export function hasActiveLedgerFilters(filters: LedgerFilters): boolean {
  return (
    filters.itemId !== null || filters.reason !== null || filters.dateFrom !== null || filters.dateTo !== null
  );
}

export type MovementType = '입고' | '출고' | '조정';

export type AdjustMode = 'set' | 'add' | 'subtract';

export interface CreateInventoryItemInput {
  name: string;
  initialStock: number;
  price: number;
  lowStockThreshold: number;
}

export interface RecordMovementInput {
  itemId: number;
  type: MovementType;
  qty: number;
  mode?: AdjustMode;
  memo?: string;
}

// 상품 정보 수정(직접 UPDATE 경로 — GRANT가 name/price/low_stock_threshold만 허용, stock은
// 원장 전용이라 이 입력에 존재할 수 없다). 필드는 전부 optional patch(codexloop R1 #2 수용):
// 바뀐 필드만 전송해 다른 탭이 고친 "무관 필드"를 stale 값으로 되덮는 것을 차단한다 — 같은
// 필드 동시 수정은 의도적으로 last-write-wins 잔존(저빈도 메타데이터에 CAS는 과설계, codexloop
// R2 합의). 최소 1개 필드는 service가 강제. name은 CreateInventoryItemInput과 동일하게 원문을
// 받고 trim은 service 책임.
export interface UpdateInventoryItemInput {
  id: number;
  name?: string;
  price?: number;
  lowStockThreshold?: number;
}

// 신규 등록 계열 다이얼로그(신규 상품 등록·미매칭 재고 등록)가 공유하는 부족 알림 기준 디폴트 —
// DB 컬럼 DEFAULT 5와 동일 값. 두 다이얼로그가 각자 '5' 리터럴을 들면 한쪽만 바뀌는 드리프트가
// 생긴다(codexloop R1 #5 수용 — 도메인 leaf인 이 파일이 단일 출처).
export const DEFAULT_LOW_STOCK_THRESHOLD = 5;

// PR5(엑셀 일괄등록): bulk_upsert_inventory_items_rpc 입력 행 — rowNo(파서 전용 미리보기 표시값)는
// 서버로 보내지 않는다(strip은 호출부 책임). 필드명은 CreateInventoryItemInput과 동일 어휘.
export interface BulkUpsertItemPayload {
  name: string;
  price: number;
  stock: number;
  lowStockThreshold: number;
}

export type BulkUpsertMode = 'upsert' | 'replace';

// bulk_upsert_inventory_items_rpc 반환 jsonb 카운트 요약(PR5 constraints 문서 #11 — 키 계약 T1 확정).
export interface BulkUpsertResult {
  created: number;
  updated: number;
  restored: number;
  softDeleted: number;
  stockAdjusted: number;
}

// PR6: get_inventory_quick_add_rpc의 items[] 원소 — overview items의 부분집합 + reserved(동일 키
// 어휘, RPC 계약 미러). /order '내 상품' 빠른 추가 그룹 전용 경량판이라 user_id/deleted_at/
// created_at/modified_at은 내려오지 않는다. available은 여기서도 파생(getAvailableStock 재사용,
// 미러 저장 금지 — PR3 제약 #7과 동일).
export interface InventoryQuickAddItem {
  id: number;
  name: string;
  norm_name: string;
  price: number;
  stock: number;
  low_stock_threshold: number;
  reserved: number;
}

// get_inventory_quick_add_rpc 응답 전체(단일 jsonb). uid NULL(비로그인)이면 items: [].
export interface InventoryQuickAdd {
  items: InventoryQuickAddItem[];
}

// PR7-2: get_inventory_today_kpis_rpc 응답 전체(단일 jsonb) — 키는 RPC 계약 그대로 snake_case
// (레포 관례 = DB 키 미러). 오늘 입고 = '입고'만, 오늘 출고 = '출고'+'주문차감' − '주문복원'
// 순액(음수 가능 — 클램프 없음, UI도 부호 그대로). uid NULL(비로그인)이면 0/0.
export interface InventoryTodayKpis {
  today_in: number;
  today_out: number;
}
